import type { UpdateUserDto, UserDto, VpnDetectedMessage } from './user.schema';
import { queryOptions, replaceEqualDeep, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFingerprintRequestIdNoCache } from '@/components/entities/analytics/components/FingerprintProvider/hooks/fingerPrintRequestId';
import { UserDeviceActivityType } from '@/components/entities/analytics/components/FingerprintProvider/types/fingerPrintType';
import { getMarketSourcePriority } from '@/libs/market/market';
import { useAppSelector } from '@/redux/hooks';
import { QueriesKeys } from '../queriesKeys';
import { userApi } from './user.api';

export function mergeUserQueryData(
  previous: UserDto | undefined,
  incoming: UserDto,
): UserDto {
  if (!previous || previous.id !== incoming.id || !previous.market || !previous.marketSource) {
    return replaceEqualDeep(previous, incoming);
  }

  const previousPriority = getMarketSourcePriority(previous.marketSource);
  const incomingPriority = getMarketSourcePriority(incoming.marketSource);
  const previousUpdatedAt = Date.parse(previous.updatedAt);
  const incomingUpdatedAt = Date.parse(incoming.updatedAt);
  const previousIsNewer = !Number.isNaN(previousUpdatedAt)
    && (Number.isNaN(incomingUpdatedAt) || previousUpdatedAt > incomingUpdatedAt);
  const previousMarketWins = previousPriority > incomingPriority
    || (previousPriority === incomingPriority && previousIsNewer);
  // Only market/marketSource may fall back to `previous`; other fields must
  // stay on `incoming`, or a stale-but-newer-looking `updatedAt` would revert
  // them (e.g. `isKycVerified`) to outdated values.
  const watermarkUser = previousIsNewer ? previous : incoming;
  const marketUser = previousMarketWins ? previous : incoming;

  return replaceEqualDeep(previous, {
    ...incoming,
    market: marketUser.market,
    marketSource: marketUser.marketSource,
    updatedAt: watermarkUser.updatedAt,
  });
}

export const userMeQueryOptions
  = queryOptions({
    queryKey: [QueriesKeys.user],
    queryFn: () => userApi.getUserMe(),
    structuralSharing: (previous, incoming) => mergeUserQueryData(
      previous as UserDto | undefined,
      incoming as UserDto,
    ),
  });

export const useUserMeQuery = () => {
  const { userSession } = useAppSelector(state => state.user);

  return useQuery({
    ...userMeQueryOptions,
    enabled: userSession,
  });
};

/** Shared so every invalidation site stays in step with the query itself. */
export const paymentMethodsQueryKey = [QueriesKeys.user, QueriesKeys.paymentMethods];

export const usePaymentMethodsQuery = () => {
  const { userSession } = useAppSelector(state => state.user);
  return useQuery({
    queryKey: paymentMethodsQueryKey,
    queryFn: () => userApi.getUserPaymentMethods(),
    enabled: userSession,
  });
};

export const useUpdateUserMeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserDto) => userApi.patchUserMe(data),
    onSuccess: (user) => {
      queryClient.setQueryData([QueriesKeys.user], user);
    },
  });
};

export const useSetAllowedSendMarketingEmailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof userApi.setAllowedSendMarketingEmails>[0]) => userApi.setAllowedSendMarketingEmails(data),
    onSuccess: (user) => {
      queryClient.setQueryData([QueriesKeys.user], user);
      queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerQuests] });
    },
  });
};

export const useVerifyPaymentMethodMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, cardId }: { userId: string; cardId: string }) => {
      return userApi.verifyPaymentMethod(userId, cardId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });
    },
  });
};

/** Recheck VPN status on demand (e.g. after the user reports turning their VPN off). */
export const useVpnCheckMutation = () => {
  const queryClient = useQueryClient();
  const { getFingerprintRequestIdNoCache } = useFingerprintRequestIdNoCache({ activityType: UserDeviceActivityType.VpnCheck });

  return useMutation({
    mutationFn: async (): Promise<VpnDetectedMessage> => {
      const requestId = await getFingerprintRequestIdNoCache();
      return userApi.checkVpn(requestId);
    },
    onSuccess: ({ isVpnDetected }) => {
      queryClient.setQueryData<UserDto>(
        [QueriesKeys.user],
        prev => (prev ? { ...prev, isVpnDetected } : prev),
      );
    },
  });
};
